import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {
  GroupScoringConfiguration,
  IndividualScoringConfiguration,
  League, Lookups,
  ScoringDefinition, TOAST_STATE,
} from "@model";
import {
  AbstractComponent,
  ConfirmationModalComponent, GroupScoringConfigEditModalComponent,
  IndividualScoringConfigEditModalComponent,
  ScoringDefinitionEditModalComponent
} from "@components";
import {TranslocoService} from "@ngneat/transloco";
import {LeagueScoringService, ToastService} from "@services";
import {ViewportScroller} from "@angular/common";
import {ActivatedRoute} from "@angular/router";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: 'app-league-scoring-configuration',
  templateUrl: './league-scoring-configuration.component.html',
  styleUrl: './league-scoring-configuration.component.scss',
  standalone: false
})
export class LeagueScoringConfigurationComponent extends AbstractComponent implements OnInit{

  @Input() league: League | undefined;
  @Input() scoringMethods: Lookups[] = [];
  @Input() rankingMethods: Lookups[] = [];
  @Input() measurementPrecisionTypes: Lookups[] = [];
  @Input() attendanceBonusTypes: Lookups[] = [];
  @Input() groupScoringTypes: Lookups[] = []

  @Input() scoringDefinitions: ScoringDefinition[] = [];
  @Input() individualScoringConfigs: IndividualScoringConfiguration [] = [];
  @Input() groupScoringConfigs: GroupScoringConfiguration[] = [];

  @Input() disableEditing: boolean = true;

  @Output() scoringConfigurationChange: EventEmitter<void> = new EventEmitter<void>();


  isScoringDefinitionCollapsed: boolean = false;
  isGroupConfigCollapsed: boolean = false;
  isIndividualConfigCollapsed: boolean = false;


  constructor(public translocoService: TranslocoService,
              public leagueScoringService: LeagueScoringService,
              private scroller: ViewportScroller,
              private route: ActivatedRoute,
              private modalService: NgbModal,
              private toastService: ToastService,
  ) {
    super();
  }

  ngOnInit() {

  }


  openScoringDefinitionModal(scoringDefinition?: ScoringDefinition, index?: number) {
    const modalRef: NgbModalRef = this.modalService.open(ScoringDefinitionEditModalComponent, {
      centered: true,
      size: 'xl'
    });
    modalRef.componentInstance.scoringDefinition = scoringDefinition;
    modalRef.componentInstance.disableEditing = this.disableEditing;
    modalRef.componentInstance.scoringMethods = this.scoringMethods;
    modalRef.componentInstance.rankingMethods = this.rankingMethods;
    modalRef.componentInstance.measurementPrecisionTypes = this.measurementPrecisionTypes;
    modalRef.componentInstance.onSave.subscribe((val: ScoringDefinition) => {
    if (this.league?.id && !this.disableEditing) {
      const handleSaveSuccess = (updated: ScoringDefinition) => {
        modalRef.close();
        if (index !== undefined  && this.scoringDefinitions.length > 0) {
          this.scoringDefinitions[index] = updated;
        }
        else {
          this.scoringDefinitions.push(updated);
        }
        this.scoringConfigurationChange.emit();
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate(val.id ? 'league.scoring.definition.edit.success' : 'league.scoring.definition.add.success'));
      }
      const handleSaveError = (errorCode?: any) => {
        modalRef.componentInstance.displayError(errorCode)
      }
      this.leagueScoringService.updateScoringDefinition(this.league!.id!, val,  handleSaveSuccess, handleSaveError)
    }
    })
  }


  private openConfirmationModal(title: string, text: string, resultAction: () => void): void {
    const modalRef = this.modalService.open(ConfirmationModalComponent, { centered: true });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.text = text;

    modalRef.result.then(
        () => resultAction()
        , (_) => {
          /// ignore
        });
  }

  openDeleteScoringDefinitionModal(scoringDefinition: ScoringDefinition, index: number): void {
    const title = `${this.translocoService.translate("league.scoring.definition.delete.dialog.title")}: ${scoringDefinition?.name ?? ''}`;
    const text = this.translocoService.translate("league.scoring.definition.delete.dialog.text");

    this.openConfirmationModal(title, text, () => this.onDeleteScoringDefinition(scoringDefinition));
  }


  onDeleteScoringDefinition(scoringDefinition: ScoringDefinition) {
    if (this.disableEditing) return;
    const handleSaveSuccess = () => {
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate('league.scoring.definition.delete.success'));
      this.scoringConfigurationChange.emit();
    }
    const handleSaveError = (errorCode?: any) => {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(errorCode === LeagueScoringService.ITEM_IN_USE ? 'league.scoring.definition.delete.error.19' : 'league.scoring.definition.delete.error'));
    }
    this.leagueScoringService.deleteScoringDefinition(scoringDefinition, handleSaveSuccess, handleSaveError)
  }

  openDeleteConfigModal(cfg: IndividualScoringConfiguration | GroupScoringConfiguration, isGroup: boolean, index: number) {
    const title = `${this.translocoService.translate("league.scoring.config.delete.dialog.title")}: ${cfg?.name ?? ''}`;
    const text = this.translocoService.translate("league.scoring.config.delete.dialog.text");
    this.openConfirmationModal(title, text, () => this.onDeleteScoringConfig(cfg, isGroup));
  }


  onDeleteScoringConfig(cfg: IndividualScoringConfiguration | GroupScoringConfiguration, isGroup: boolean) {
    if (this.disableEditing) return
    const handleSaveSuccess = () => {
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate('league.scoring.config.delete.success'));
      this.scoringConfigurationChange.emit();
    }
    const handleSaveError = (errorCode?: any) => {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(errorCode === LeagueScoringService.ITEM_IN_USE ? 'league.scoring.config.delete.error.19' : 'league.scoring.config.delete.error'));
    }
    if (isGroup) {
        this.leagueScoringService.deleteGroupScoringConfiguration(cfg as GroupScoringConfiguration, handleSaveSuccess, handleSaveError)
    } else {
        this.leagueScoringService.deleteIndividualScoringConfiguration(cfg as IndividualScoringConfiguration, handleSaveSuccess, handleSaveError)
    }
  }


  handleDropdownClick(event: Event): void {
    event.stopPropagation();
  }


  openGroupConfigModal(cfg?: GroupScoringConfiguration, index?: number) {
    if (this.scoringDefinitions.length > 0) {
      const modalRef: NgbModalRef = this.modalService.open(GroupScoringConfigEditModalComponent, {
        centered: true,
        size: 'xl'
      });
      modalRef.componentInstance.scoringConfiguration = cfg;
      modalRef.componentInstance.scoringDefinitions = this.scoringDefinitions;
      modalRef.componentInstance.groupScoringTypes = this.groupScoringTypes;
      modalRef.componentInstance.disableEditing = this.disableEditing;
      modalRef.componentInstance.onSave.subscribe((val: GroupScoringConfiguration) => {
        if (this.league?.id && !this.disableEditing) {
          const handleSaveSuccess = (updated: GroupScoringConfiguration) => {
            modalRef.close();
            if (index !== undefined  && this.groupScoringConfigs.length > 0) {
              this.groupScoringConfigs[index] = updated;
            }
            else {
              this.groupScoringConfigs.push(updated);
            }
            this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate(val.id ? 'league.scoring.group.config.edit.success' : 'league.scoring.group.config.add.success'));
            this.scoringConfigurationChange.emit();
          }
          const handleSaveError = (errorCode?: any) => {
            modalRef.componentInstance.displayError(errorCode)
          }
          this.leagueScoringService.updateGroupScoringConfiguration(this.league!.id!, val,  handleSaveSuccess, handleSaveError)
        }
      })
    }
  }


  openIndividualConfigModal(cfg?: IndividualScoringConfiguration, index?: number) {
    if (this.scoringDefinitions.length > 0) {
      const modalRef: NgbModalRef = this.modalService.open(IndividualScoringConfigEditModalComponent, {
        centered: true,
        size: 'xl',
      });
      modalRef.componentInstance.scoringConfiguration = cfg;
      modalRef.componentInstance.scoringDefinitions = this.scoringDefinitions;
      modalRef.componentInstance.attendanceBonusTypes = this.attendanceBonusTypes;
      modalRef.componentInstance.disableEditing = this.disableEditing;
      modalRef.componentInstance.onSave.subscribe((val: IndividualScoringConfiguration) => {
        if (this.league?.id && !this.disableEditing) {
          const handleSaveSuccess = (updated: IndividualScoringConfiguration) => {
            modalRef.close();
            if (index !== undefined  && this.individualScoringConfigs.length > 0) {
              this.individualScoringConfigs[index] = updated;
            }
            else {
              this.individualScoringConfigs.push(updated);
            }
            this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate(val.id ? 'league.scoring.individual.config.edit.success' : 'league.scoring.individual.config.add.success'));
            this.scoringConfigurationChange.emit();
          }
          const handleSaveError = (errorCode?: any) => {
            modalRef.componentInstance.displayError(errorCode)
          }
          this.leagueScoringService.updateIndividualScoringConfiguration(this.league!.id!, val,  handleSaveSuccess, handleSaveError)
        }
      })
    }
  }

}
